import { DownOutlined } from "@ant-design/icons"
import { Checkbox } from "antd"
import React, { memo } from "react"
import { useTranslation } from "react-i18next"

import { cn } from "../../../../utils/cn.ts"
import {
  type InheritedRow,
  type MatrixColumn,
  type MatrixRow,
  getRowDisclosureId
} from "../../helpers/layout.ts"
import { getDisplayEntry } from "../../helpers/utils.ts"
import type {
  ColumnKey,
  OnBulkChange,
  PermissionsFlat,
  SectionRecord
} from "../../types.ts"
import type { PermissionFeatures } from "../../types/permissions.types.ts"
import { PermissionCheckbox } from "./PermissionCheckbox.tsx"
import { usePermissionsEditor } from "./editor-context.ts"
import { useWatchedPermissions } from "./use-watched-permissions.ts"

interface PermissionsMatrixProps {
  sectionKey: string
  sectionTitle: string
  sections: PermissionsFlat
  columns: readonly MatrixColumn[]
  rows: MatrixRow[]
  inherited: InheritedRow[]
  onBulkChange: OnBulkChange
}

const LABEL_COLUMN = "minmax(13rem, 1.6fr)"
const ACTION_COLUMN = "minmax(5.5rem, 1fr)"

// Reads the same cells the old "Toggle column" switch did: every grid row of
// the section, whatever the search shows. Inherited rows are not among them.
const useColumnState = (
  sections: PermissionsFlat,
  rows: MatrixRow[],
  colKey: ColumnKey
) => {
  const paths = rows.flatMap((row) => {
    const actionKey = row.cells[colKey]
    return actionKey
      ? [{ sectionKey: row.sectionKey, subKey: row.subKey, actionKey }]
      : []
  })
  const values = useWatchedPermissions(paths, sections)
  return {
    checked: values.every(Boolean),
    indeterminate: values.some(Boolean) && !values.every(Boolean),
    present: values.length > 0
  }
}

const BulkColumnCheckbox = ({
  sectionTitle,
  sections,
  rows,
  col,
  onBulkChange
}: {
  sectionTitle: string
  sections: PermissionsFlat
  rows: MatrixRow[]
  col: MatrixColumn
  onBulkChange: OnBulkChange
}) => {
  const { t } = useTranslation()
  const { disabled, isUpdating } = usePermissionsEditor()
  const { checked, indeterminate, present } = useColumnState(
    sections,
    rows,
    col.key
  )

  if (!present) return <span aria-hidden />

  // A section can show rows stored under another, so one press may set
  // several stored sections, each through its own cascade.
  const toggle = (value: boolean) => {
    const bySection = new Map<string, SectionRecord>()
    rows.forEach((row) => {
      bySection.set(row.sectionKey, {
        ...bySection.get(row.sectionKey),
        [row.subKey]: row.actions
      })
    })
    bySection.forEach((bulkRows, storedIn) =>
      onBulkChange(
        storedIn as keyof PermissionFeatures,
        col.key,
        bulkRows,
        value
      )
    )
  }

  return (
    <Checkbox
      checked={checked}
      indeterminate={indeterminate}
      disabled={disabled || isUpdating}
      aria-label={t("permissions.editor.selectAllColumn", {
        column: t(col.labelKey),
        section: sectionTitle
      })}
      onChange={(e) => toggle(e.target.checked)}
    />
  )
}

const NotSupportedCell = ({ column, row }: { column: string; row: string }) => {
  const { t } = useTranslation()
  const text = t("permissions.editor.notSupported", { column, row })
  return (
    <span className="text-gray-300 select-none" title={text}>
      <span aria-hidden>—</span>
      <span className="sr-only">{text}</span>
    </span>
  )
}

// Never registered with the form: the right is saved where its own module
// keeps it, and this row only mirrors it.
const InheritedRowView = ({
  row,
  sections,
  columns,
  gridTemplateColumns
}: {
  row: InheritedRow
  sections: PermissionsFlat
  columns: readonly MatrixColumn[]
  gridTemplateColumns: string
}) => {
  const { t } = useTranslation()
  const shown = columns.flatMap((col) => {
    const actionKey = row.cells[col.key]
    return actionKey ? [{ col, actionKey }] : []
  })
  const values = useWatchedPermissions(
    shown.map(({ actionKey }) => ({
      sectionKey: row.sectionKey,
      subKey: row.subKey,
      actionKey
    })),
    sections
  )
  const note = t("permissions.editor.inheritedFrom", {
    section: row.sourceTitle
  })

  return (
    <div
      role="row"
      className="grid items-center min-h-12 px-4 border-b border-border last:border-b-0 bg-muted/40"
      style={{ gridTemplateColumns }}
    >
      <div role="rowheader" className="flex flex-col py-2 pr-3">
        <span className="font-medium text-gray-700">{row.label}</span>
        <span className="text-xs text-gray-500">{note}</span>
      </div>
      {columns.map((col) => {
        const at = shown.findIndex((cell) => cell.col.key === col.key)
        const columnLabel = t(col.labelKey)
        return (
          <div
            role="cell"
            key={col.key}
            className="flex items-center justify-center py-2"
          >
            {at === -1 ? (
              <NotSupportedCell column={columnLabel} row={row.label} />
            ) : (
              <Checkbox
                checked={values[at]}
                disabled
                title={note}
                aria-label={`${row.label} — ${columnLabel}`}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

const MatrixRowView = ({
  sectionKey,
  row,
  columns,
  gridTemplateColumns
}: {
  sectionKey: string
  row: MatrixRow
  columns: readonly MatrixColumn[]
  gridTemplateColumns: string
}) => {
  const { t } = useTranslation()
  const { displayMap, isOpen, setOpen } = usePermissionsEditor()
  const disclosureId = getRowDisclosureId(sectionKey, row.subKey)
  const hasCells = Object.keys(row.cells).length > 0
  const open = isOpen(disclosureId, !hasCells)
  const panelId = `${disclosureId}-panel`
  const rowTooltip = getDisplayEntry(displayMap, row.subKey)?.tooltip

  return (
    <div role="rowgroup" className="border-b border-border last:border-b-0">
      <div
        role="row"
        className="grid items-center min-h-12 px-4 hover:bg-muted/70"
        style={{ gridTemplateColumns }}
      >
        <div role="rowheader" className="flex items-center gap-3 py-2 pr-3">
          <span className="font-medium text-gray-900" title={rowTooltip}>
            {row.label}
          </span>
          {row.extras.length > 0 && (
            <button
              type="button"
              aria-expanded={open}
              aria-controls={panelId}
              aria-label={t("permissions.editor.additionalOptionsFor", {
                row: row.label
              })}
              onClick={() => setOpen(disclosureId, !open)}
              className="inline-flex items-center gap-1.5 rounded px-1.5 py-1 text-xs font-medium text-primary hover:bg-primary-bg focus-visible:outline-2 focus-visible:outline-primary"
            >
              <DownOutlined
                aria-hidden
                className={cn(
                  "text-[10px] transition-transform",
                  open && "rotate-180"
                )}
              />
              {t("permissions.editor.additionalOptions")}
            </button>
          )}
        </div>
        {columns.map((col) => {
          const actionKey = row.cells[col.key]
          const columnLabel = t(col.labelKey)
          const caption = actionKey
            ? getDisplayEntry(displayMap, actionKey)?.name
            : undefined
          return (
            <div
              role="cell"
              key={col.key}
              className="flex flex-col items-center justify-center gap-0.5 py-2"
            >
              {actionKey ? (
                <>
                  <PermissionCheckbox
                    sectionKey={row.sectionKey}
                    subKey={row.subKey}
                    actionKey={actionKey}
                    initialValue={row.actions[actionKey] ?? false}
                    ariaLabel={caption || `${row.label} — ${columnLabel}`}
                  />
                  {caption && (
                    <span className="text-[11px] leading-tight text-center text-gray-500">
                      {caption}
                    </span>
                  )}
                </>
              ) : (
                <NotSupportedCell column={columnLabel} row={row.label} />
              )}
            </div>
          )
        })}
      </div>

      {row.extras.length > 0 && (
        // Kept mounted while closed: a collapsed option is still a registered
        // form field, so it is saved and keeps its unsaved value.
        <div
          id={panelId}
          hidden={!open}
          className="mx-4 mb-3 rounded-md border border-border bg-muted px-4 py-3"
        >
          <div className="grid gap-x-8 gap-y-2.5 sm:grid-cols-2">
            {row.extras.map((ref) => (
              <PermissionCheckbox
                key={ref.actionKey}
                sectionKey={ref.sectionKey}
                subKey={ref.subKey}
                actionKey={ref.actionKey}
                initialValue={row.actions[ref.actionKey] ?? false}
              >
                {ref.label}
              </PermissionCheckbox>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export const PermissionsMatrix = memo(function PermissionsMatrix({
  sectionKey,
  sectionTitle,
  sections,
  columns,
  rows,
  inherited,
  onBulkChange
}: PermissionsMatrixProps) {
  const { t } = useTranslation()
  const gridTemplateColumns = `${LABEL_COLUMN} repeat(${columns.length}, ${ACTION_COLUMN})`

  return (
    // No overflow of its own: the panel scrolls both ways, so the header row
    // sticks to the panel's scrollport rather than to a box that never scrolls.
    <div className="min-w-[40rem] rounded-lg border border-border bg-white">
      <div
        role="table"
        aria-label={`${sectionTitle} — ${t("permissions.editor.mainActions")}`}
      >
        {/* Sticky travel stops at the panel's padding edge, so the offset
            takes that padding back, or rows scroll through the strip above. */}
        <div
          role="rowgroup"
          className="sticky -top-4 z-[2] rounded-t-lg bg-muted sm:-top-6"
        >
          <div
            role="row"
            className="grid items-center px-4 h-10 border-b border-border text-xs font-medium text-gray-600"
            style={{ gridTemplateColumns }}
          >
            <div role="columnheader">
              {t("permissions.editor.permissionColumn")}
            </div>
            {columns.map((col) => (
              <div role="columnheader" key={col.key} className="text-center">
                {t(col.labelKey)}
              </div>
            ))}
          </div>
          <div
            role="row"
            className="grid items-center px-4 h-10 border-b border-border bg-white"
            style={{ gridTemplateColumns }}
          >
            <div role="rowheader" className="text-xs text-gray-500">
              {t("permissions.editor.selectAllRow")}
            </div>
            {columns.map((col) => (
              <div role="cell" key={col.key} className="flex justify-center">
                <BulkColumnCheckbox
                  sectionTitle={sectionTitle}
                  sections={sections}
                  rows={rows}
                  col={col}
                  onBulkChange={onBulkChange}
                />
              </div>
            ))}
          </div>
        </div>

        {rows.map((row) => (
          <MatrixRowView
            key={`${row.sectionKey}.${row.subKey}`}
            sectionKey={sectionKey}
            row={row}
            columns={columns}
            gridTemplateColumns={gridTemplateColumns}
          />
        ))}
        {inherited.length > 0 && (
          <div role="rowgroup">
            {inherited.map((row) => (
              <InheritedRowView
                key={`${row.sectionKey}.${row.subKey}`}
                row={row}
                sections={sections}
                columns={columns}
                gridTemplateColumns={gridTemplateColumns}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
})
